import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import React, { forwardRef, ReactNode, useMemo, useRef } from 'react';
import { ListChildComponentProps } from 'react-window';
import { VirtualAutocompleteListbox } from './VirtualAutocompleteListBox';

interface AutocompleteWithVirtualListProps<
  T,
  Multiple extends boolean | undefined,
> extends AutocompleteProps<T, Multiple, true, false> {
  VirtualListRow: (props: ListChildComponentProps) => JSX.Element;
  itemSize?: number;
  itemsToDisplay?: number;
  itemOffset?: number;
  loadMoreItems?: (
    startIndex: number,
    stopIndex: number,
  ) => void | Promise<void>;
  hasNextPage?: boolean;
  renderListHeader?: () => ReactNode;
}

export function AutocompleteWithVirtualList<
  T,
  Multiple extends boolean | undefined,
>({
  VirtualListRow,
  itemSize,
  itemsToDisplay,
  itemOffset,
  loadMoreItems,
  hasNextPage,
  renderListHeader,
  ...autoCompleteProps
}: AutocompleteWithVirtualListProps<T, Multiple>) {
  const hasNextPageRef = useRef(hasNextPage);
  const loadMoreItemsRef = useRef(loadMoreItems);

  hasNextPageRef.current = hasNextPage;
  loadMoreItemsRef.current = loadMoreItems;

  const ListboxComponent = useMemo(
    () =>
      forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
        function ListboxComponent(props, ref) {
          return (
            <VirtualAutocompleteListbox
              {...props}
              ref={ref}
              VirtualListRow={VirtualListRow}
              itemSize={itemSize}
              itemsToDisplay={itemsToDisplay}
              itemOffset={itemOffset}
              loadMoreItems={loadMoreItemsRef.current}
              hasNextPage={hasNextPageRef.current}
              renderListHeader={renderListHeader}
            />
          );
        },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Autocomplete {...autoCompleteProps} ListboxComponent={ListboxComponent} />
  );
}
